import React from 'react';
import { Box } from '@mui/material';
import './error-boundary.css';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Developer-facing region id, used only for console logging. */
  region?: string;
  /** When this value changes (e.g. the route), a caught error is cleared. */
  resetKey?: unknown;
  /** Compact inline card instead of a centered page-level message. */
  inline?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: unknown;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error(`[ErrorBoundary${this.props.region ? `:${this.props.region}` : ''}]`, error, info.componentStack);
    }
  }

  componentDidUpdate(prev: ErrorBoundaryProps) {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const message = this.state.error instanceof Error ? this.state.error.message : undefined;
    const inline = this.props.inline;

    const stack = import.meta.env.DEV && this.state.error instanceof Error ? this.state.error.stack : undefined;

    return (
      <Box
        role="alert"
        aria-live="assertive"
        sx={{ display: 'flex', justifyContent: 'center', py: inline ? 0 : { xs: 4, md: 8 } }}
      >
        <section className={`ens-error${inline ? ' is-inline' : ''}`}>
          <div className="ens-error-head">
            <span className="ens-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.3 3.9 2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
                <path d="M12 9.5v4M12 17h.01" />
              </svg>
            </span>
            <div>
              <h3>This part of the page couldn’t be displayed</h3>
              <p className="ens-error-sub">Try again, or go to another page. If it keeps happening, reload the app.</p>
            </div>
          </div>
          {message && <p className="ens-msg">{message}</p>}
          {stack && <ErrorTrace stack={stack} />}
          <div className="ens-actions">
            <button className="ens-btn" type="button" onClick={this.handleRetry}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 11a8 8 0 1 0-2.3 5.7" />
                <path d="M20 4v7h-7" />
              </svg>
              <span>Try again</span>
            </button>
            {!inline && (
              <button className="ens-link" type="button" onClick={() => window.location.reload()}>
                Reload app
              </button>
            )}
          </div>
        </section>
      </Box>
    );
  }
}

function ErrorTrace({ stack }: { stack: string }) {
  const [open, setOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [first, ...rest] = stack.split('\n');
  const body = rest.join('\n');
  const [, before = body, name = '', after = ''] = body.match(/^(\s*at )(\S+)([\s\S]*)$/) ?? [];

  const copy = () => {
    navigator.clipboard?.writeText(stack).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="ens-trace" data-open={open}>
      <div className="ens-trace-bar">
        <button className="ens-trace-toggle" type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
          <span>{open ? 'Hide technical details' : 'Show technical details'}</span>
        </button>
        <button className="ens-copy" type="button" onClick={copy}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="9" y="9" width="11" height="11" rx="2" />
            <path d="M5 15V6a2 2 0 0 1 2-2h9" />
          </svg>
          <span>{copied ? 'Copied' : 'Copy'}</span>
        </button>
      </div>
      {open && (
        <pre>
          <span className="err">{first}</span>
          {'\n'}
          {before}
          <span className="hit">{name}</span>
          {after}
        </pre>
      )}
    </div>
  );
}

export default ErrorBoundary;
